/**
 * Frontend Sentry init — no-op when REACT_APP_SENTRY_DSN is unset.
 * Called from src/index.js BEFORE ReactDOM.render so early-render errors
 * are captured.
 *
 * The release tag (`tin-frontend@<sha>`) is injected at build time via the
 * REACT_APP_GIT_SHA env var (see package.json start/build scripts).
 */
import * as Sentry from '@sentry/react';

export const initSentry = () => {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) return false;

  const sha = process.env.REACT_APP_GIT_SHA || 'unknown';

  Sentry.init({
    dsn,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || 'production',
    release: `tin-frontend@${sha}`,
    // 10% performance traces matches backend SENTRY_TRACES_SAMPLE_RATE.
    tracesSampleRate: 0.1,
    // Session replays disabled by default — turn on later if useful.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    // Don't ship browser extension noise or third-party script errors.
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
    ],
  });
  return true;
};

/**
 * Forward an unexpected error to Sentry, ignoring known 4xx HTTP responses.
 *
 * Sprinkle into catch blocks where the user already gets a toast but you
 * still want Sentry visibility for genuine outages (e.g. dashboard data-load
 * failures). Per /app/memory/CATCH_AUDIT.md backlog item.
 *
 * Examples:
 *   catch (e) { toast.error('...'); reportUnexpected(e, { route: '/staff/dashboard' }); }
 *
 * No-op when Sentry is unconfigured (initSentry returned false).
 */
export const reportUnexpected = (error, context = {}) => {
  if (!error) return;
  // Skip well-formed 4xx-class HTTP failures — those are expected user/input
  // errors and would just create noise in Sentry.
  const status = error?.response?.status;
  if (status && status >= 400 && status < 500) return;
  try {
    Sentry.captureException(error, { extra: context });
  } catch (_) {
    /* Sentry not initialized — silent no-op */
  }
};

/**
 * Send a non-error event to Sentry (e.g. "user encountered stale-bundle
 * version drift"). Goes through `Sentry.captureMessage` with level='info'
 * so it doesn't pollute the error-tracking signal — these events show up
 * in Sentry's separate Issues > level:info stream.
 *
 * No-op when Sentry is unconfigured.
 */
export const reportInfo = (message, context = {}) => {
  if (!message) return;
  try {
    Sentry.captureMessage(message, { level: 'info', extra: context });
  } catch (_) {
    /* Sentry not initialized — silent no-op */
  }
};

export { Sentry };
